import type {
  FieldId,
  KeyAndInnerKeys,
  NotUndefined,
} from "@shared/frontend/hyper-table";
import {
  repo,
  type ComparisonValueFilter,
  type ContainsStringValueFilter,
  type EntityDbNames,
  type EntityFilter,
  type MembersOnly,
  type ObjectMembersOnly,
  type ValueFilter,
} from "remult";
import type { ClassType } from "remult/classType.js";
import { sqlRelations, sqlRelationsFilter } from "remult/internals";
import type { ArrayItemType } from "remult/src/data-providers/sql-relations";
import type { EntityMetadata, idType } from "remult/src/remult3/remult3";
import { getRelationFieldInfo } from "./relation-info-member";
import { jsonFilterToEntityFilter } from "./sql-json";

export class SqlRelationFilterToMany<
  myEntity,
  relationKey extends keyof myEntity,
  toEntity = ArrayItemType<myEntity[relationKey]>,
> {
  constructor(
    private myEntity: ClassType<myEntity>,
    private relationField: relationKey,
  ) {}

  some(where?: EntityFilter<toEntity>): EntityFilter<toEntity> {
    return sqlRelationsFilter(this.myEntity)[this.relationField].some(
      where as any,
    ) as EntityFilter<toEntity>;
  }

  every(where?: EntityFilter<toEntity>): EntityFilter<toEntity> {
    return {
      $not: this.some({
        $not: where,
      }),
    } as EntityFilter<toEntity>;
  }

  none(where?: EntityFilter<toEntity>): EntityFilter<toEntity> {
    return {
      $not: this.some(where),
    };
  }
}

export class SqlRelationFilterToOne<
  myEntity,
  relationKey extends keyof myEntity,
  toEntity = myEntity[relationKey],
> {
  constructor(
    private myEntity: ClassType<myEntity>,
    private relationField: relationKey,
  ) {}

  is(where: EntityFilter<toEntity>): EntityFilter<toEntity> {
    return sqlRelationsFilter(this.myEntity)[this.relationField].some(
      where as any,
    ) as EntityFilter<toEntity>;
  }

  isNot(where: EntityFilter<toEntity>): EntityFilter<toEntity> {
    return {
      $not: this.is(where),
    } as EntityFilter<toEntity>;
  }
}

export interface RelationalEntityFilterConditions<entity> {
  /**
   * The `$or` operator specifies that at least one of the conditions in the array must be true.
   * It is used to combine multiple filters where the entity satisfies any of the conditions.
   *
   */
  $or?: RelationalOrJsonEntityFilter<entity>[];

  /**
   * The `$and` operator specifies that all of the conditions in the array must be true.
   * It is used to combine multiple filters where the entity must satisfy all of the conditions.
   *
   */
  $and?: RelationalOrJsonEntityFilter<entity>[];

  /**
   * The `$not` operator negates the filter condition, meaning the entity must **not** match the condition.
   * This is used to exclude entities that meet the specified filter.
   *
   */
  $not?: RelationalOrJsonEntityFilter<entity>;
}

export interface IdRelationalFilter<entity> {
  /**
   * The `$id` operator is used for filtering based on the relation's id. this is more efficient than { $is: { id: ... } }
   *
   * Example:
   *
   * ```ts
   * // Input: Search for users of given ids
   * const query = {
   *   user: {
   *      $id: {
   *        $in: [...]
   *      }
   *   }
   * };
   * // this query will return all users that their id is in the list
   * ```
   */
  $id?: ValueFilter<idType<Exclude<entity, undefined>>>;
}

export interface RelationOrJsonFilterToOne<entity> {
  /**
   * The `$is` operator is used for the matching of values
   *
   * if `null` is provided, than it returns all elements that the relation does not exist OR for json fields, all those that the field does not exist
   *
   * Example:
   *
   * ```ts
   * // Input: Search for users where the address is "Jerusalem" AND their name is "Bar"
   * const query = {
   *   user: {
   *     $is: {
   *       name: "Bar",
   *       address: "Jerusalem"
   *     }
   *   }
   * };
   * // this query will return all users that their name is "Bar" and their address is "Jerusalem"
   * ```
   */
  $is?: null | RelationalOrJsonEntityFilter<Exclude<entity, undefined>>;

  /**
   * The `$isNot` operator is used to check that the values of a value are **NOT** within a specified set of values
   *
   * if `null` is provided, than it returns all elements that the relation exist OR for json fields, all those that the field exist
   *
   * Example:
   *
   * ```ts
   * // Input: Search for users that their names don't start with the letter "N"
   * const query = {
   *   user: {
   *     $isNot: {
   *       name: {
   *          $startsWith: "N"
   *        }
   *     }
   *   }
   * };
   * // this query will return all users that their name **does not** start with "N"
   *
   * //this is the same as:
   * const query = {
   *   user: {
   *     $is: {
   *       name: {
   *          $not: {
   *            $startsWith: "N"
   *          }
   *        }
   *     }
   *   }
   * };
   * ```
   */
  $isNot?: null | RelationalOrJsonEntityFilter<Exclude<entity, undefined>>;
}

export interface RelationOrJsonFilterToMany<entity> {
  /**
   * The `$some` operator checks if **at least one** element in the `toMany` relation satisfies a given condition
   *
   * Example:
   *
   * ```ts
   * // Input: Search for opportunities that at least one of their teams has a status of "Urgent"
   * const query = {
   * opportunity: {
   *     teams: {
   *       $some: {
   *          team: {
   *            $is: {
   *              status: "Urgent"
   *            }
   *          }
   *       }
   *     }
   *   }
   * };
   * // this query will result in all opportunities that have at least one team with status "Urgent"
   * ```
   */
  $some?: ArrayItemType<entity> extends number | Date | undefined | null
    ? ComparisonValueFilter<ArrayItemType<entity>>
    : ArrayItemType<entity> extends string | undefined
      ?
          | ArrayItemType<entity>
          | (ContainsStringValueFilter &
              ComparisonValueFilter<ArrayItemType<entity>>)
      : ArrayItemType<entity> extends boolean | undefined | null
        ? ValueFilter<boolean>
        : RelationalOrJsonEntityFilter<
            Exclude<ArrayItemType<entity>, undefined>
          >;

  /**
   * The `$every` operator checks if **all** elements in the `toMany` relation satisfy a given condition
   *
   * if `null` is provided, than it returns all elements that do not have any element related to them OR for JSON fields, returns all elements that have an empty array or do not exist at all
   *
   * Example:
   *
   * ```ts
   * // Input: Search for opportunities that all of their teams have a status of "Pending"
   * const query = {
   * opportunity: {
   *     teams: {
   *       $every: {
   *          team: {
   *            $is: {
   *              status: "Pending"
   *            }
   *          }
   *       }
   *     }
   *   }
   * };
   * // this query will result in all opportunities that all of their teams have the same status - "Pending"
   * ```
   */
  $every?:
    | null
    | (ArrayItemType<entity> extends number | Date | undefined | null
        ? ComparisonValueFilter<ArrayItemType<entity>>
        : ArrayItemType<entity> extends string | undefined
          ?
              | ArrayItemType<entity>
              | (ContainsStringValueFilter &
                  ComparisonValueFilter<ArrayItemType<entity>>)
          : ArrayItemType<entity> extends boolean | undefined | null
            ? ValueFilter<boolean>
            : RelationalOrJsonEntityFilter<
                Exclude<ArrayItemType<entity>, undefined>
              >);

  /**
   * The `$none` operator checks if **all** elements in the `toMany` relation **don't** satisfy a given condition
   *
   * Example:
   *
   * ```ts
   * // Input: Search for opportunities that all of their teams *don't* have a status of "Pending"
   * const query = {
   * opportunity: {
   *     teams: {
   *       $none: {
   *          team: {
   *            $is: {
   *              status: "Pending"
   *            }
   *          }
   *       }
   *     }
   *   }
   * };
   * // this query will result in all opportunities that none of their teams have the status - "Pending"
   * ```
   */
  $none?: ArrayItemType<entity> extends number | Date | undefined | null
    ? ComparisonValueFilter<ArrayItemType<entity>>
    : ArrayItemType<entity> extends string | undefined
      ?
          | ArrayItemType<entity>
          | (ContainsStringValueFilter &
              ComparisonValueFilter<ArrayItemType<entity>>)
      : ArrayItemType<entity> extends boolean | undefined | null
        ? ValueFilter<boolean>
        : RelationalOrJsonEntityFilter<
            Exclude<ArrayItemType<entity>, undefined>
          >;
}

export type RelationOrJsonFilter<entity> =
  Exclude<entity, undefined> extends any[]
    ? RelationOrJsonFilterToMany<entity>
    : RelationOrJsonFilterToOne<entity> &
        (Exclude<entity, undefined> extends { id: idType<any> }
          ? IdRelationalFilter<entity>
          : {});

export type RelationalOrJsonEntityFilter<entity> = {
  [K in keyof MembersOnly<entity>]?: MembersOnly<entity>[K] extends
    | Date
    | undefined
    ? EntityFilter<entity>[K]
    : MembersOnly<entity>[K] extends Record<any, any> | undefined
      ? RelationOrJsonFilter<MembersOnly<entity>[K]>
      : EntityFilter<entity>[K];
} & RelationalEntityFilterConditions<entity>;

export function getChildEntity<PEntity>(
  parent: ClassType<PEntity>,
  field: keyof PEntity & string,
) {
  return getRelationFieldInfo(repo(parent).metadata.fields.find(field))!
    .toEntity;
}

export function isRelation<PEntity>(
  parent: ClassType<PEntity>,
  field: keyof PEntity & string,
) {
  return Boolean(
    getRelationFieldInfo(repo(parent).metadata.fields.find(field))?.type,
  );
}

export function isJsonField<entity>(
  parent: ClassType<entity>,
  field: keyof entity & string,
) {
  const info = repo(parent).metadata.fields.find(field);
  return (
    info?.valueConverter?.fieldTypeInDb === "json" &&
    (info.options as any)?.allowedKeys
  );
}

export function getJsonFieldWhiteList<entity>(
  parent: ClassType<entity>,
  field: keyof entity & string,
): string[] {
  const info = repo(parent).metadata.fields.find(field);
  return (info.options as any)?.allowedKeys;
}

function resolveRelationFilter<ParentEntity>(
  parent: ClassType<ParentEntity>,
  key: keyof ParentEntity & string,
  filter:
    | (RelationOrJsonFilter<MembersOnly<ParentEntity>> &
        EntityFilter<ParentEntity>)
    | RelationOrJsonFilter<MembersOnly<ParentEntity>>,
): EntityFilter<ParentEntity>[] {
  const entityFilter: EntityFilter<ParentEntity>[] = [];

  Object.keys(filter).forEach((_k) => {
    const k = _k as keyof typeof filter;

    switch (k) {
      case "$every":
        entityFilter.push(
          new SqlRelationFilterToMany(parent, key).every(
            filterWithRelationsAndJson(getChildEntity(parent, key))(
              filter[k] as any,
            ),
          ) as any,
        );
        break;
      case "$some":
        entityFilter.push(
          new SqlRelationFilterToMany(parent, key).some(
            filterWithRelationsAndJson(getChildEntity(parent, key))(
              filter[k] as any,
            ),
          ) as any,
        );
        break;
      case "$none":
        entityFilter.push(
          new SqlRelationFilterToMany(parent, key).none(
            filterWithRelationsAndJson(getChildEntity(parent, key))(
              filter[k] as any,
            ),
          ) as any,
        );
        break;
      case "$is":
        if (filter[k] !== null) {
          // If filter[key] is null then we wan't to run a "NOT EXISTS" query to check if the relation does not exist
          entityFilter.push(
            new SqlRelationFilterToOne(parent, key).is(
              filterWithRelationsAndJson(getChildEntity(parent, key))(
                filter[k] as any,
              ),
            ) as any,
          );
        } else {
          entityFilter.push(
            ...resolveRelationFilter(parent, key, {
              $isNot: filter[k],
            } as any),
          );
        }
        break;
      case "$isNot":
        entityFilter.push(
          new SqlRelationFilterToOne(parent, key).isNot(
            filterWithRelationsAndJson(getChildEntity(parent, key))(
              filter[k] as any,
            ),
          ) as any,
        );
        break;
      case "$id":
        entityFilter.push({
          [key]: {
            $id: filter[k],
          },
        } as any);
        break;
      default:
        console.error(
          `${k.toString()} is not a valid operator for relational filtering`,
        );
    }
  });

  return entityFilter;
}

/**
 * `filterWithRelations` is a higher-order function that returns a `hyperFilter` function.
 * The `hyperFilter` function is used to build and apply filters to a parent entity,
 * including support for relational fields. The filters can be applied to fields,
 * relations, and conditional logic like `$and`, `$or`, and `$not`.
 *
 * This function enables querying of nested or related entities, handling both direct
 * field filters and relational filters (e.g., filters on associated entities).
 *
 * @template ParentEntity - The type of the parent entity.
 *
 * @param parent - The class type of the parent entity.
 *
 *
 * @returns -
 * A function (`hyperFilter`) that takes a filter for relational entities and returns
 * an entity filter for the parent entity.
 *
 * @example
 * // Example usage
 * const filter = filterWithRelations(Organization);
 * const query = filter({
 *   name: "Tech Co",
 *   $and: [
 *     { address: "Tech Street" },
 *     {
 *       employees: {
 *         $some: { role: "Developer" }
 *       }
 *     }
 *   ]
 * });
 *
 * // The above example builds a query to filter organizations with the name "Tech Co",
 * // where the address is "Tech Street" and at least one employee has the role "Developer".
 */
export function filterWithRelationsAndJson<ParentEntity>(
  parent: ClassType<ParentEntity>,
) {
  /**
   * `hyperFilter` is a function that builds a filter query for a parent entity,
   * including handling relational filters. It separates the relational filters,
   * logical conditions (`$and`, `$or`, `$not`), and regular field filters,
   * and returns a combined query object for filtering related entities.
   *
   * This function applies the provided filters, resolving nested relations and
   * ensuring all conditions (such as `$and`, `$or`, `$not`) are properly applied.
   *
   * @template ParentEntity - The type of the parent entity.
   *
   * @param filter - The filter conditions to apply to the parent entity.
   *
   * @returns  - The final entity filter, including both field and relational filters, with logical conditions applied.
   *
   * @example
   * // Example usage:
   * const filter = hyperFilter({
   *   name: "Tech Co",
   *   $and: [
   *     { address: "Tech Street" },
   *     {
   *       employees: {
   *         $some: { role: "Developer" }
   *       }
   *     }
   *   ]
   * });
   *
   * // The above example builds a query for an organization with name "Tech Co",
   * // where the address is "Tech Street" and at least one employee has the role "Developer".
   */
  return function filterRelationsAndJson(
    filter: RelationalOrJsonEntityFilter<ParentEntity>,
  ): EntityFilter<ParentEntity> {
    const relationFilters: Partial<
      Record<
        keyof ParentEntity,
        | (RelationOrJsonFilter<MembersOnly<ParentEntity>> &
            EntityFilter<ParentEntity>)
        | RelationOrJsonFilter<MembersOnly<ParentEntity>>
      >
    > = {};

    const conditionalRelational: Partial<
      Record<
        keyof RelationalEntityFilterConditions<any>,
        typeof relationFilters
      >
    >[] = [];

    const jsonFilters: {
      filter: Partial<
        Record<
          keyof ParentEntity,
          | (RelationOrJsonFilter<MembersOnly<ParentEntity>> &
              EntityFilter<ParentEntity>)
          | RelationOrJsonFilter<MembersOnly<ParentEntity>>
        >
      >;
      whiteList: string[];
    }[] = [];

    const fieldFilter: EntityFilter<ParentEntity> = {};

    for (const _k in filter) {
      const key = _k as keyof typeof filter;
      const f = filter[key];
      if (key === "$and" || key === "$not" || key === "$or") {
        conditionalRelational.push({ [key]: f });
      } else if (isRelation<ParentEntity>(parent, key)) {
        relationFilters[key] = f as any;
      } else if (isJsonField(parent, key)) {
        jsonFilters.push({
          filter: { [key]: f } as any,
          whiteList: getJsonFieldWhiteList(parent, key),
        });
        jsonFilters[key] = f as any;
      } else {
        fieldFilter[key] = f as any;
      }
    }

    return {
      $and: [
        fieldFilter,
        ...Object.entries(relationFilters).flatMap(([k, v]) =>
          resolveRelationFilter(parent, k as any, v as any),
        ),
        ...conditionalRelational.flatMap((entry) =>
          Object.entries(entry).map(([k, v]) => ({
            [k]: Array.isArray(v)
              ? v.map((inner) => filterRelationsAndJson(inner))
              : filterRelationsAndJson(v as any),
          })),
        ),
        ...jsonFilters.map((f) =>
          jsonFilterToEntityFilter(f.filter as any, parent, f.whiteList),
        ),
      ],
    } as any;
  };
}
/**
 * ## Create a custom `sqlExpression` for your field ✨🔧
 *
 * ### Helper for simplifying the process of creating an `sqlExpression` based field, making it type safe and readable.
 *
 * ---
 * Example:
 * ---
 * Let's say we want to create a field that holds the amount of `associations` a `user` has where his `status = "active"`.
 *
 * ```ts
 *
 *  *@Fields.integer({
 *    sqlExpression: () =>
 *      sqlHyperRelations(User, {
 *        associations: {
 *          $select: (dbNames) => "COUNT(*)", //dbNames hold the dbNames of the selected entity
 *          $where: {
 *            status: "active",
 *          },
 *        },
 *      }),
 *  })
 *  activeAssociationCount!: number;
 *
 * ```
 */
export async function sqlHyperRelations<Entity>(
  entity: ClassType<Entity>,
  hyperRelation: SqlHyperRelation<Entity>,
): Promise<string> {
  const relations = sqlRelations(entity);

  if (Object.keys(hyperRelation).length > 1) {
    throw new Error(
      `Hyper relation can only have one key-value set! Got ${JSON.stringify(hyperRelation)}`,
    );
  }

  const key = Object.keys(hyperRelation)[0] as keyof typeof hyperRelation;
  const entry = hyperRelation[key];

  if (entry && typeof entry === "object" && "$select" in (entry as any)) {
    return relations[key as keyof typeof relations].$subQuery(
      (entry as any).$select,
      {
        where: filterWithRelationsAndJson(getChildEntity(entity, key as any))(
          (entry as any).$where ?? {},
        ) as any,
      },
    );
  }
  return await relations[key as keyof typeof relations].$subQuery(() =>
    sqlHyperRelations(getChildEntity(entity, key as any), entry as any),
  );
}

export type FieldsProxy<Entity, OriginalEntity = Entity> =
  Exclude<NotUndefined<Entity>, Date> extends Record<any, any>
    ? {
        [K in keyof Entity & string]: NotUndefined<Entity[K]> extends Record<
          string,
          any
        >
          ? NotUndefined<Entity[K]> extends any[]
            ? FieldsProxy<NotUndefined<Entity[K]>[number], OriginalEntity>
            : FieldsProxy<NotUndefined<Entity[K]>, OriginalEntity>
          : FieldId<OriginalEntity>;
      }
    : FieldId<OriginalEntity>;

type NonArrayKeys<T> = {
  [K in keyof T]: T[K] extends any[] ? never : K;
}[keyof T];

export type SqlRelationsProxy<Entity, OriginalEntity = Entity> = {
  [K in NonArrayKeys<Entity> & string]: Exclude<
    NotUndefined<Entity[K]>,
    Date
  > extends Record<any, any>
    ? SqlRelationsProxy<NotUndefined<Entity[K]>, OriginalEntity>
    : (
        filter?: RelationalOrJsonEntityFilter<Entity>,
      ) => SqlRelationsProxyReturn<Entity, OriginalEntity>;
} & ((
  filter?: RelationalOrJsonEntityFilter<Entity>,
) => SqlRelationsProxyReturn<Entity, OriginalEntity>);

export interface SqlRelationsProxyReturn<Entity, OriginalEntity> {
  isRelation: boolean;
  path: FieldId<OriginalEntity>;
  filter: RelationalOrJsonEntityFilter<Entity>;
  finalEntity: ClassType<Entity>;
}

export function createFieldProxyForEntity<Entity>(
  entity: ClassType<Entity>,
  path?: FieldId<Entity>,
) {
  return new Proxy(
    {},
    {
      get(_, prop) {
        if (typeof prop === "symbol") return undefined;
        if (isRelation(entity, prop as keyof Entity & string)) {
          return createFieldProxyForEntity(
            getChildEntity(entity, prop as keyof Entity & string),
            (path ? `${path}.${prop}` : String(prop)) as FieldId<Entity>,
          );
        } else return path ? `${path}.${prop}` : prop;
      },
    },
  ) as FieldsProxy<Entity>;
}

export function createFilterProxyForSqlRelation<Entity>(
  entity: ClassType<Entity>,
  path?: FieldId<Entity>,
  target: any = {},
) {
  return new Proxy(target, {
    get(_, prop) {
      if (typeof prop === "symbol") return undefined;

      const finalPath = (path ? `${path}.${prop}` : prop) as FieldId<Entity>;

      if (isRelation(entity, prop as keyof Entity & string)) {
        const child = getChildEntity(entity, prop as keyof Entity & string);
        return createFilterProxyForSqlRelation(child, finalPath, function <
          ChildEntity,
        >(filter: RelationalOrJsonEntityFilter<ChildEntity> = {}) {
          return {
            isRelation: true,
            path: finalPath,
            filter,
            finalEntity: child,
          };
        });
      } else {
        return function <ChildEntity>(
          filter: RelationalOrJsonEntityFilter<ChildEntity> = {},
        ) {
          return {
            isRelation: false,
            path: finalPath,
            filter,
            finalEntity: entity,
          };
        };
      }
    },
  }) as SqlRelationsProxy<Entity>;
}

export function sqlRelationalField<Entity, TargetEntity = unknown>(
  getField: (
    fields: SqlRelationsProxy<Entity>,
  ) => SqlRelationsProxyReturn<TargetEntity, Entity>,
) {
  return async function (metadata: EntityMetadata<Entity>) {
    const entity = metadata.entityType;

    const { filter, isRelation, path } = getField(
      createFilterProxyForSqlRelation(entity),
    );

    const pathArr = path.split(".");

    const finalName = isRelation ? "*" : pathArr.pop()!;

    const select: (
      dbNames: EntityDbNames<Exclude<Entity, undefined>>,
    ) => string | Promise<string> = (names) =>
      isRelation ? `json_agg(${names.toString()})` : names.$dbNameOf(finalName);

    const where = filter;

    const hyperRelation: SqlHyperRelation<Entity> = {};
    let pointer: Record<string, any> = hyperRelation;

    pathArr.forEach((path) => {
      pointer[path] = {};
      pointer = pointer[path];
    });

    pointer.$select = select;
    pointer.$where = where;

    return await sqlHyperRelations(entity, hyperRelation);
  };
}

type SqlHyperQueryResult<Entity> =
  Exclude<Entity, undefined> extends any[]
    ? SqlHyperQueryResult<ArrayItemType<Exclude<Entity, undefined>>>
    : {
        /**
         * ### What to select from the entity?
         *
         *
         * ---
         *
         * #### Example:
         *
         * ---
         * ```ts
         *
         * { $select: (dbNames) => `${dbNames.id}, ${dbNames.name}` } // here we are only selecting the id and name from the required entity
         *
         * ```
         *
         */
        $select: (
          dbNames: EntityDbNames<Exclude<Entity, undefined>>,
        ) => string | Promise<string>;

        /**
         * ## A `HyperFilter🌟✨` object, used to filter with relations and json values to filter your sqlExpression
         */
        $where?: RelationalOrJsonEntityFilter<Exclude<Entity, undefined>>;
      };

export type SqlHyperRelation<Entity> = {
  [K in keyof ObjectMembersOnly<Entity>]?:
    | SqlHyperQueryResult<Entity[K]>
    | SqlHyperRelationsFinally<Entity[K]>;
};

type SqlHyperRelationsFinally<Entity> =
  Exclude<Entity, undefined> extends Record<any, any>
    ? Exclude<Entity, undefined> extends any[]
      ? SqlHyperRelation<ArrayItemType<Exclude<Entity, undefined>>>
      : SqlHyperRelation<Exclude<Entity, undefined>>
    : never;

export function generateHyperFilterFromPath<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
  finalFilter: any,
) {
  const paths = field.split(".");
  const finalPath = paths.pop()!;

  const filter: RelationalOrJsonEntityFilter<Entity> = {};
  let currentFilter = filter;
  let currentEntity = entity;
  paths.forEach((path) => {
    const type = getRelationFieldInfo(
      repo(currentEntity).metadata.fields.find(path)!,
    )?.type;
    const isToMany = type === "toMany";
    if (isToMany) {
      currentFilter[path as keyof typeof filter] = { $some: {} } as any;
      currentFilter = (currentFilter[path as keyof typeof filter] as any).$some;
    } else {
      currentFilter[path as keyof typeof filter] = { $is: {} } as any;
      currentFilter = (currentFilter[path as keyof typeof filter] as any).$is;
    }
    currentEntity = getChildEntity(currentEntity, path as any);
  });
  (currentFilter as any)[finalPath] = finalFilter;
  return filter;
}

export function EntityHyperFilter<Entity>(
  entity: ClassType<Entity>,
  filter: RelationalOrJsonEntityFilter<Entity>,
): EntityFilter<Entity> {
  return (entity as any).hyperFilter(filter);
}
